/**
 * SyringesModal Component
 *
 * Main modal showing a list of culture syringes purchased from external suppliers.
 * Displays syringes in collapsible sections by status (In-date, Expired, Depleted)
 * with optional filtering by strain.
 */

import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useCultureSyringes } from '@/hooks/useCultureSyringes'
import AddSyringeForm from './AddSyringeForm'
import SyringeDetailModal from './SyringeDetailModal'
import { Syringe, Plus, Loader2, Package, ChevronDown } from 'lucide-react'

// Status badge variants
const getStatusBadge = (status) => {
  switch (status) {
    case 'received':
      return <Badge variant="secondary">Received</Badge>
    case 'in_use':
      return <Badge variant="default">In Use</Badge>
    case 'exhausted':
      return <Badge variant="outline">Exhausted</Badge>
    case 'expired':
      return <Badge variant="destructive">Expired</Badge>
    case 'disposed':
      return <Badge variant="outline" className="text-muted-foreground">Disposed</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

// Calculate days until expiry and get colour class
const getExpiryInfo = (expiryDate) => {
  if (!expiryDate) return { days: null, color: '' }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expiry = new Date(expiryDate)
  const diffTime = expiry - today
  const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  let color = 'text-green-600'
  if (days <= 0) color = 'text-destructive'
  else if (days <= 7) color = 'text-amber-600'
  else if (days <= 14) color = 'text-amber-500'

  return { days, color }
}

// Get variant classes for section header
const getSectionHeaderClasses = (variant) => {
  switch (variant) {
    case 'success':
      return 'bg-green-50 hover:bg-green-100 border-green-200'
    case 'destructive':
      return 'bg-red-50 hover:bg-red-100 border-red-200'
    case 'muted':
      return 'bg-muted/50 hover:bg-muted border-border'
    default:
      return 'bg-muted/50 hover:bg-muted border-border'
  }
}

const getBadgeVariant = (variant) => {
  switch (variant) {
    case 'success':
      return 'default'
    case 'destructive':
      return 'destructive'
    case 'muted':
      return 'secondary'
    default:
      return 'secondary'
  }
}

// Collapsible section component for displaying grouped syringes
function SyringeSection({ title, count, isOpen, onToggle, syringes, onRowClick, variant }) {
  if (count === 0) {
    return null
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <button
          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${getSectionHeaderClasses(variant)}`}
        >
          <div className="flex items-center gap-2">
            <span className="font-medium">{title}</span>
            <Badge variant={getBadgeVariant(variant)} className="h-5 px-2">
              {count}
            </Badge>
          </div>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch Code</TableHead>
              <TableHead>Strain</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Volume</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {syringes.map((syringe) => {
              const expiryInfo = getExpiryInfo(syringe.expiry_date)
              const volumePercent = (syringe.volume_remaining_ml / syringe.volume_ml) * 100

              return (
                <TableRow
                  key={syringe.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onRowClick(syringe)}
                >
                  <TableCell className="font-mono font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onRowClick(syringe)
                      }}
                      className="hover:underline hover:text-odin-blue cursor-pointer text-left"
                    >
                      {syringe.supplier_batch_code}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{syringe.strain?.code}</span>
                      <span className="text-muted-foreground text-sm">
                        {syringe.strain?.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{syringe.supplier?.name}</TableCell>
                  <TableCell>
                    {new Date(syringe.received_date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>
                        {new Date(syringe.expiry_date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                      {expiryInfo.days !== null && (
                        <span className={`text-xs font-medium ${expiryInfo.color}`}>
                          {expiryInfo.days <= 0 ? 'Expired' : `${expiryInfo.days}d`}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className={volumePercent <= 25 ? 'text-amber-600' : ''}>
                        {syringe.volume_remaining_ml}/{syringe.volume_ml}ml
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(syringe.status)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  )
}

export default function SyringesModal({
  open,
  onOpenChange
}) {
  const [strainFilter, setStrainFilter] = useState('all')
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedSyringeId, setSelectedSyringeId] = useState(null)
  const [openSections, setOpenSections] = useState({
    indate: true,
    expired: false,
    depleted: false,
  })

  // Always fetch all syringes, filter client-side
  const { syringes, isLoading, refetch } = useCultureSyringes({
    includeExhausted: true,
  })

  // Get unique strains for filter dropdown
  const uniqueStrains = useMemo(() => {
    const strainMap = new Map()
    syringes.forEach(s => {
      if (s.strain?.code && !strainMap.has(s.strain.code)) {
        strainMap.set(s.strain.code, s.strain)
      }
    })
    return Array.from(strainMap.values()).sort((a, b) =>
      a.code.localeCompare(b.code)
    )
  }, [syringes])

  // Filter by strain and group by status
  const groupedSyringes = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // First filter by strain if selected
    const filtered = strainFilter === 'all'
      ? syringes
      : syringes.filter(s => s.strain?.code === strainFilter)

    // Group into status categories
    const indate = filtered.filter(s =>
      (s.status === 'received' || s.status === 'in_use') &&
      new Date(s.expiry_date) >= today
    )
    const expired = filtered.filter(s =>
      new Date(s.expiry_date) < today &&
      s.status !== 'exhausted'
    )
    const depleted = filtered.filter(s => s.status === 'exhausted')

    return { indate, expired, depleted }
  }, [syringes, strainFilter])

  // Toggle collapsible section
  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  // Handle syringe added
  const handleSyringeAdded = () => {
    refetch()
  }

  // Handle row click
  const handleRowClick = (syringe) => {
    setSelectedSyringeId(syringe.id)
  }

  // Handle detail modal close
  const handleDetailClose = () => {
    setSelectedSyringeId(null)
    // Refetch in case something changed
    refetch()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Syringe className="h-5 w-5" />
                Culture Syringes
              </DialogTitle>
              {syringes.length > 0 && (
                <Button
                  variant="create"
                  size="sm"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Syringe
                </Button>
              )}
            </div>
          </DialogHeader>

          {/* Strain Filter */}
          {uniqueStrains.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filter by strain:</span>
              <Select
                value={strainFilter}
                onValueChange={setStrainFilter}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All strains</SelectItem>
                  {uniqueStrains.map(strain => (
                    <SelectItem key={strain.code} value={strain.code}>
                      {strain.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Collapsible Sections */}
          <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6 space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : syringes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No syringes yet</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first syringe
                </Button>
              </div>
            ) : (
              <>
                {/* In-date Section */}
                <SyringeSection
                  title="In-date"
                  count={groupedSyringes.indate.length}
                  isOpen={openSections.indate}
                  onToggle={() => toggleSection('indate')}
                  syringes={groupedSyringes.indate}
                  onRowClick={handleRowClick}
                  variant="success"
                />

                {/* Expired Section */}
                <SyringeSection
                  title="Expired"
                  count={groupedSyringes.expired.length}
                  isOpen={openSections.expired}
                  onToggle={() => toggleSection('expired')}
                  syringes={groupedSyringes.expired}
                  onRowClick={handleRowClick}
                  variant="destructive"
                />

                {/* Depleted Section */}
                <SyringeSection
                  title="Depleted"
                  count={groupedSyringes.depleted.length}
                  isOpen={openSections.depleted}
                  onToggle={() => toggleSection('depleted')}
                  syringes={groupedSyringes.depleted}
                  onRowClick={handleRowClick}
                  variant="muted"
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Syringe Form */}
      <AddSyringeForm
        open={showAddForm}
        onOpenChange={setShowAddForm}
        onSuccess={handleSyringeAdded}
      />

      {/* Syringe Detail Modal */}
      <SyringeDetailModal
        open={!!selectedSyringeId}
        onOpenChange={(open) => {
          if (!open) handleDetailClose()
        }}
        syringeId={selectedSyringeId}
      />
    </>
  )
}
